import styles from "./ChattingTerms.module.scss"

import React, { useState, useEffect } from "react"

import { useRecoilState } from "recoil"
import { BlacklistChattingRole } from "streamerChatting/enums"
import { useBlock } from "streamerChatting/hooks/useBlock"
import { showTermsState } from "streamerChatting/store"
import checkIconChecked from "@shared/assets/images/check-circle-fill.svg"
import checkIconUnchecked from "@shared/assets/images/check-circle-line.svg"
import alertIcon from "@shared/assets/images/popup-alert.svg"

// 아이콘 색은 --icon-checked / --icon-unchecked 토큰으로 지정하고 SVG 는 마스크로만 사용
const getIconMaskStyle = (checked: boolean) =>
  ({ "--icon-mask": `url(${checked ? checkIconChecked : checkIconUnchecked})` } as React.CSSProperties)

function ChattingTerms({ onClose }: { onClose: () => void }) {
  const [termsState, setTermsState] = useRecoilState(showTermsState)
  const { isAllBlocking, handleToggleBlockingChat } = useBlock()
  const [toastMessage, setToastMessage] = useState("")

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(""), 2000)
      return () => clearTimeout(timer)
    }
  }, [toastMessage])

  const handleStep1Check = () => {
    setTermsState((prev) => ({
      ...prev,
      isStep1Agreed: !prev.isStep1Agreed,
    }))
  }

  const handleStep2Check = () => {
    setTermsState((prev) => ({
      ...prev,
      isStep2Agreed: !prev.isStep2Agreed,
    }))
  }

  const handleNextStep = () => {
    if (termsState.step === 1) {
      if (!termsState.isStep1Agreed) {
        setToastMessage("체크박스를 체크해주세요")
        return
      }
      setTermsState((prev) => ({ ...prev, step: 2 }))
    } else if (termsState.step === 2) {
      if (!termsState.isStep2Agreed) {
        setToastMessage("체크박스를 체크해주세요")
        return
      }
      if (isAllBlocking) handleToggleBlockingChat(BlacklistChattingRole.ALL)
      handleClose()
    }
  }

  const handleCancel = () => {
    if (!isAllBlocking) handleToggleBlockingChat(BlacklistChattingRole.ALL)
    handleClose()
  }

  const handleClose = () => {
    setTermsState({
      isOpen: false,
      step: 1,
      isStep1Agreed: false,
      isStep2Agreed: false,
    })
    onClose()
  }

  return (
    <div
      className={styles.root}
      style={{ transform: !termsState.isOpen ? "translateX(-100%)" : "" }}
    >
      {termsState.step === 1 ? (
        <div className={styles.stepContainer}>
          <div className={styles.progressBadge}>1 / 2</div>
          <h1 className={styles.title}>무료방송 양방향 채팅 시 필수 동의</h1>
          <p className={styles.body}>
            무료 방송 진행 시 '개별성 없는 조언(다수인을 대상으로 일방적으로 이루어지는 투자에 관한
            조언)'과 관련하여 아래와 같이 동의합니다.
          </p>
          <div className={styles.checkboxGroup}>
            <button
              className={styles.checkbox}
              onClick={handleStep1Check}
              aria-pressed={termsState.isStep1Agreed}
            >
              <span
                className={`${styles.checkIcon} ${termsState.isStep1Agreed ? styles.checked : ""}`}
                style={getIconMaskStyle(termsState.isStep1Agreed)}
              />
            </button>
            <label className={styles.checkLabel} onClick={handleStep1Check}>
              본인은 채팅 참여자의 <span className={styles.danger}>1:1 상담을 하지 않을 것에</span> 동의합니다.
            </label>
          </div>
          <div className={styles.buttonGroup}>
            <button className={styles.buttonCancel} onClick={handleCancel}>
              취소
            </button>
            <button
              className={`${styles.buttonNext} ${termsState.isStep1Agreed ? "" : styles.inactive}`}
              onClick={handleNextStep}
            >
              다음
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.stepContainer}>
          <div className={styles.progressBadge}>2 / 2</div>
          <h1 className={styles.title}>무료방송 양방향 채팅 시 필수 동의</h1>
          <div className={styles.checkboxGroup}>
            <button
              className={styles.checkbox}
              onClick={handleStep2Check}
              aria-pressed={termsState.isStep2Agreed}
            >
              <span
                className={`${styles.checkIcon} ${termsState.isStep2Agreed ? styles.checked : ""}`}
                style={getIconMaskStyle(termsState.isStep2Agreed)}
              />
            </button>
            <label className={styles.checkLabel} onClick={handleStep2Check}>
              위반 시 와우넷 파트너스 계약서 제 8조 및 제 9조에 따라{" "}
              <span className={styles.danger}>계약 해지와 함께 위약금을</span> 부과함에 동의합니다.
            </label>
          </div>
          <p className={styles.warningText}>취소 시에는 양방향 채팅이 불가합니다.</p>
          <div className={styles.buttonGroup}>
            <button className={styles.buttonCancel} onClick={handleCancel}>
              취소
            </button>
            <button
              className={`${styles.buttonNext} ${termsState.isStep2Agreed ? "" : styles.inactive}`}
              onClick={handleNextStep}
            >
              확인
            </button>
          </div>
        </div>
      )}

      {toastMessage && (
        <div className={styles.toastWrap}>
          <div className={styles.toast}>
            <img src={alertIcon} alt="alert" className={styles.toastIcon} />
            <span className={styles.toastText}>{toastMessage}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default ChattingTerms
